import _ from "lodash";
import { ValueMappings } from "./enum.js";

export const dateFormatter = new Intl.DateTimeFormat(
  "en-GB",
  // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
  // for config docs
  {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  },
);

export const dateTimeFormatter = new Intl.DateTimeFormat(
  "en-GB",
  // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
  // for config docs
  {
    year: "numeric",
    month: "short",
    day: "2-digit",
    
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",

    timeZoneName: "shortOffset"
  },
);

export const numberFormatter = new Intl.NumberFormat(
  // this is french because 1,234.56 is stupid
  // 1 234,56 makes so much more sense... fight me 💢
  "fr-FR",
  { style: "decimal", useGrouping: "auto" }
)
 
/**
 * Formats the date
 * @param {number | string | Date} date the raw date, needs to be compatible with Date() object initialiser
 * @returns {string} formatted date
 */
export function formatDate(date) {
  try {
    return dateFormatter.format(new Date(date));
  } catch (err) {
    return "NaT";
  }
}

/**
 * Formats the date
 * @param {number | string | Date} date the raw date, needs to be compatible with Date() object initialiser
 * @returns {string} formatted date
 */
export function formatDateTime(date) {
  try {
    return dateTimeFormatter.format(new Date(date));
  } catch (err) {
    return "NaT";
  }
}

export function formatNumber(num) {
  try {
    // use decimal point
    // but we keep french format so we have grouping space cuz 1,234.56 is ****
    return numberFormatter.format(num).replace(',', '.');
    } catch (err) {
    return "NaN";
  }
}



/**
 * rounds priority score to specified decimal places
 * @param {number} score the priority score
 * @param {number} dp specified decimal places
 * @returns {string} rounded priority score
 */
export function roundPriorityScore(score, dp=3) {
  return parseFloat(score).toFixed(dp);
}

/**
 * Transforms analytics data received from the backend into a format compatible with Recharts.
 * @param {object} data - The raw analytics data retrieved from the backend.
 * @returns {object} The data in a format that Recharts can understand.
 */
export function transformAnalyticsData(data) {
  // i hate myself
  /* THE PROBLEM
   *   Data is sent in a specific way. This way is good, very good, very sane, very pogchamp.
   *   Unfortunately, Recharts doesn't agree that my data is in a good format, rechart pmo sm bro.
   * THE SOLUTION
   *   The data needs to be mapped so that Recharts can understand it (it's a wee bit dumb...)
   *   The data needs to be in the format:
   *     [{value: X, field1_value: Y_1, field2_value: Y_2}, ...]
   *   But it's in the format:
   *     [{field1: <field1 value>, data: [{value: X, count: Y_1}, ...]}, {...}]
   *   So this function has to somehow map it...
   * 
   *   Sample output:
   *     [{age: {min: 18, max: 25}, "operationType: 0": 69, "operationType: 1": 420}, ...]
   *   This seems simple... right? WRONG
   *   Oh, and if it wasn't bad enough, we need to deal with MANY (2) different types of data...fml
   */

  if (!data[0]) return null;

  // first we check whether the object is grouped...
  // and we then get the groupBy field name (or null if it's not grouped)
  let groupField = Object.keys(data[0]).length > 1 ? _.findKey(data[0], f => typeof f !== "object" || _.has(f, ["min"])) ?? null : null;
  // this uses the assumption that the data[0] has the format {groupBy: val, fieldName: [...data]}

  // then extract the data field name
  let dataField = _.findKey(data[0], Array.isArray) ?? null;

  // need to have a data field
  if (!dataField)
    throw new Error("Received invalid data in analytics transformation function.")

  // now we check whether the data is discrete or continuous
  // for this we simply check if there's a 'value' field or not
  let discrete = _.has(data[0][dataField][0], "value");

  // invert cuz i might use it later
  // this means that there's a min and max field
  let continuous = !discrete;

  let resultMap = new Map();  

  const getDataKey = (record) => {
    // function for grabbing the key from an object in data[x].<field>[y]
    if (_.has(record, "min"))
      return record.min + (record.max ? `\u2013${record.max}` : "+");
    else
      return record.value;
  }

  // go through each group of data
  for (const group of data) {
    // groupValue just means each value of groupBy field

    // get the name of each group, or set to null if ungrouped
    let groupName = groupField
      ? ( typeof group[groupField] === "string"
          ? group[groupField]
          : getDataKey(group[groupField])
        )
      : null;
    
    // and now we cast it to a string... WHY DID I DO THIS???????
    let groupKey = groupField !== null ? `${groupName}` : "count";

    _.forEach(group[dataField], (entry) => {
      let dataKey = getDataKey(entry);
      resultMap[dataKey] = _.merge(resultMap[dataKey], { [groupKey]: entry.count })
    })
  }

  // flatten key into the object
  let result = _.transform(resultMap, (res, val, key) => {
    res.push({ name: ValueMappings?.[dataField]?.[key] ?? key, ...val })
  }, [])

  // sort result
  result = _.sortBy(result, val => {
    // check if it converts to a number
    let numeric = parseInt(val.name.split("\u2013+", 2)[0]);

    // did it?
    if (!_.isNaN(numeric))
      return numeric;
    // no, so sort by name
    return val.name;
  });

  return result;

}

/** 
 * simple, returns a list of all the keys used to group the data 
 * @param {object} data - Analytics data
 * @returns {string[]} a list of all the keys used to classify the data
 */
export function getAllGroupKeys(data) {
  // new set
  let keys = new Set();

  // add all keys in each record of data that aren't called "name"
  _.forEach(data, (record) => _.remove(_.keysIn(record), val => val !== "name").forEach(v => keys.add(v)));
  
  // return kill yourself (im so funny haha)
  return [...keys].sort();
}

/**
 * Normalises an object so that all group keys are present
 * @param {object} data - transformed analytics data
 * @returns {object} a version where each object has all available keys present
 */
export function normaliseGroupData(data) {
  // get all them keys in the thingy
  let keys = getAllGroupKeys(data);

  // lodash, yet another tool that makes my code unreadable :D
  _.forEach(data, record => { // loop through records
    // get missing keys by running difference on all keys and record's keys (yeet name)
    let missingKeys = _.difference(keys, _.keys(record)).filter(el => el !== "name");
    // merge these missing keys with a 0 value
    record = _.merge(record, ...missingKeys.map(key => ({ [key]: 0 })));
    // sort the keys
    record = _.pick(record, _.keys(record).sort());
  })

  // return the data that we fixed up in-place anyway
  return data;
}